<template>
  <div class="conversations__chats">
    <NotificationsDialog v-model="showNotificationsDialog"/>
    <div class="conversations__header">
      <div class="d-flex align-center">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon
                   small
                   v-on="on"
                   @click="$router.push({ name: 'ConversationMetrics' })">
              <img src="https://storage.googleapis.com/keybe/dashboard-icons/conversations-metrics.svg"
                   style="width: 28px"
                   alt="Metrics icon">
            </v-btn>
          </template>
          <span>{{ $t('sidebar.conversationMetrics') }}</span>
        </v-tooltip>
      </div>

      <div class="conversations__chats--actions">
        <v-badge :content="unreadMessages"
                 :value="unreadMessages"
                 color="red"
                 overlap>
          <v-tooltip bottom>
            <template v-slot:activator="{on}">
              <v-btn elevation="0"
                     :color="displayMethod === 'withAdvisor' ? 'white kbTableHeaders--text' : ''"
                     :text="displayMethod !== 'withAdvisor'"
                     :dark="!loadingRooms"
                     :disabled="loadingRooms"
                     fab
                     small
                     v-on="on"
                     class="mx-1"
                     @click="changeDisplayMethod('withAdvisor')">
                <v-icon :color="displayMethod === 'withAdvisor' ? '' : 'white'">
                  mdi-face-agent
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('general.yourConversations') }}</span>
          </v-tooltip>
        </v-badge>

        <v-tooltip bottom
                   :key="method.value"
                   v-for="method in displayMethodsItems">
          <template v-slot:activator="{on}">
            <v-btn elevation="0"
                   :color="displayMethod === method.value ? 'white kbTableHeaders--text' : ''"
                   :text="displayMethod !== method.value"
                   :dark="!loadingRooms"
                   :disabled="loadingRooms"
                   fab
                   small
                   v-on="on"
                   class="mx-1"
                   @click="changeDisplayMethod(method.value)">
              <v-icon :color="displayMethod === method.value ? '' : 'white'">
                {{method.icon}}
              </v-icon>
            </v-btn>
          </template>
          <span>{{method.text}}</span>
        </v-tooltip>
      </div>
    </div>

    <div style="padding-left: 6px">
      <v-text-field prepend-inner-icon="mdi-magnify"
                    :placeholder="`${$t('general.search')}`"
                    @keyup="updateSearch()"
                    @click:clear="clearSearch()"
                    solo
                    flat
                    hide-details
                    clearable
                    v-model="search">
      </v-text-field>
    </div>

    <v-divider/>

    <div v-if="chats.length === 0"
         class="no-conversations-container">
      <v-icon class="conversations-info-icon">
        mdi-check
      </v-icon>
      {{$t('general.noPendingConversations')}}
    </div>

    <div class="chats-container"
         v-else>
      <Chat :chat="chat"
            :key="chat._id + index"
            @select="selectRoom(chat)"
            v-for="(chat, index) in chats">
      </Chat>
    </div>

    <v-divider v-if="chatsCount > 0"/>

    <div style="display: flex; align-items: center; padding: 16px">
      <AutoStarts/>

      <v-btn :text="!pagination.showAll"
             color="#C92239"
             :dark="!loadingRooms"
             :disabled="loadingRooms"
             elevation="0"
             @click="showAll()"
             v-if="isSuperAdmin && displayMethod === 3">
        <v-icon left v-if="pagination.showAll">
          mdi-check
        </v-icon>
        Todas
      </v-btn>

      <v-spacer/>

      <span v-if="!pagination.showAll && chatsCount > 0">
        {{(pagination.page * pagination.itemsPerPage - pagination.itemsPerPage) + 1}} - {{(pagination.page * pagination.itemsPerPage - pagination.itemsPerPage) + chats.length}} de {{chatsCount}}
        <v-btn class="ml-2"
               @click="previousPage()"
               icon>
            <v-icon>
                feather-chevron-left
            </v-icon>
        </v-btn>

        <v-btn @click="nextPage()"
               icon>
            <v-icon>
                feather-chevron-right
            </v-icon>
        </v-btn>
      </span>
    </div>
  </div>
</template>

<script>
import { EventBus } from '@/eventBus.js'
import { mapGetters, mapState } from 'vuex'
import AutoStarts from '@/pages/App/Conversations/AutoStarts.vue'
import Chat from '@/pages/App/Conversations/Chat.vue'
import notifications from '@/libs/notifications'
import NotificationsDialog from '@/pages/App/Conversations/components/NotificationsDialog.vue'
import Push from 'push.js'
export default {
  components: { AutoStarts, Chat, NotificationsDialog },
  computed: {
    ...mapGetters(['administratorRoles', 'isSuperAdmin']),
    ...mapState({
      selectedRoom: state => state.conversations.selectedRoom,
      hideNotificationsAlert: state => state.auth.hideNotificationsAlert,
      socket: state => state.conversations.socket
    }),
    displayMethodsItems () {
      return [
        { text: this.$t('general.waitingForAttention'), value: 'readyForAdvisor', icon: 'mdi-clock' },
        { text: this.$t('general.closedConversations'), value: 'ended', icon: 'mdi-check' },
        { text: this.$t('general.allConversations'), value: 'all', icon: 'mdi-account-group' }
      ]
    }
  },
  async created () {
    const hasPermissions = ['ROLE_ADMIN', 'FILTER_ALL_CHATS', 'ROLE_CONVERSATIONS', 'VIEW_ALL_CHATS'].some(r => this.administratorRoles.includes(r))
    const routerQuery = this.$route.query
    if (hasPermissions && routerQuery && routerQuery.type && routerQuery.value) {
      this.displayMethod = 'all'
      this.search = routerQuery.value
    }
    this.getRooms()

    EventBus.$on('conversationsChangeFilter', filter => {
      this.displayMethod = filter
    })

    EventBus.$on('newMessage', filter => {
      if (this.displayMethod !== 'withAdvisor') {
        this.unreadMessages++
      }
    })

    EventBus.$on('conversationsUpdateRoomLastMessageSender', () => {
      const chat = this.chats.find(item => {
        return item._id === this.selectedRoom
      })

      chat.lastMessageFrom = 'advisor'
    })

    if (!Push.Permission.has()) {
      Push.Permission.request(() => {
        notifications.success('Notificaciones activadas')
      }, () => {
        if (!this.hideNotificationsAlert) {
          this.showNotificationsDialog = true
        }
      })
    }
  },
  data: () => ({
    audio: null,
    channel: ['web', 'support', 'whatsApp', 'facebookMessenger'],
    chats: [],
    chatsCount: 0,
    displayMethod: 'withAdvisor',
    loadingRooms: false,
    pagination: {
      itemsPerPage: 50,
      page: 1,
      showAll: false
    },
    search: '',
    showNotificationsDialog: false,
    timer: null,
    unreadMessages: 0
  }),
  methods: {
    changeDisplayMethod (displayMethod) {
      this.displayMethod = displayMethod
      this.pagination.showAll = false
      this.pagination.itemsPerPage = 50
      this.pagination.page = 1
      this.getRooms()
      this.$store.commit('SET_SELECTED_ROOM', '')
      this.$store.commit('SET_ROOM_INFO', '')
    },
    clearSearch () {
      this.search = ''
      this.updateSearch()
    },
    findRoom () {
      const selectedRoomInfo = this.chats.find(item => {
        return item._id === this.selectedRoom
      })

      this.$store.commit('SET_ROOM_INFO', selectedRoomInfo || '')
    },
    async getRooms () {
      if (this.loadingRooms) return

      try {
        this.loadingRooms = true

        const data = {
          advancedSearch: {
            displayMethod: this.displayMethod
          },
          pagination: this.pagination,
          search: this.search
        }

        const response = await this.$store.dispatch('getRooms', data)
        this.chats = response.rooms.rows
        this.chatsCount = response.rooms.count
        this.loadingRooms = false

        if (!this.selectedRoom) return
        this.findRoom()
      } catch (e) {
        console.log(e)
        this.loadingRooms = false
      }
    },
    initSocketEvents () {
      this.socket.on('newConversation', () => {
        if (Push.Permission.has()) {
          Push.create('Keybe', {
            body: 'Nueva conversación en Keybe',
            icon: 'https://keybe.atlassian.net/s/-zafpfn/b/23/020fc94ba405728b3714ea364e03f01c/_/jira-favicon-scaled.png',
            timeout: 10000,
            onClick: function () {
              window.focus()
              this.close()
            }
          })
        }

        const promise = this.audio.play()

        if (promise !== undefined) {
          promise.catch(() => {
            notifications.success('Recibiste mensajes nuevos, Has click para activar sonido')
          })
        }

        if (this.displayMethod !== 'onlyEnded') this.getRooms()
      })

      this.socket.on('waitingRoom', data => {
        this.getRooms()
        if (data.message && data.message.sender === 'listener') {
          const actualRooms = this.chats.find(chat => data.roomId === chat._id)
          if (actualRooms) {
            this.audio.play()
          }
        }
      })
    },
    leaveRoom () {
      if (!this.selectedRoom) return
      this.socket.emit('leaveRoom', { roomId: this.selectedRoom })
    },
    nextPage () {
      if (this.pagination.page * this.pagination.itemsPerPage < this.chatsCount) {
        ++this.pagination.page
        this.getRooms()
      }
    },
    previousPage () {
      if (this.pagination.page > 1) {
        --this.pagination.page
        this.getRooms()
      }
    },
    selectRoom (room) {
      this.leaveRoom()
      this.$store.commit('SET_ROOM_INFO', room)
      this.$store.commit('SET_SELECTED_ROOM', room._id)
      this.$emit('changeCurrentComponent', 'ConversationContainer')
    },
    updateSearch () {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
      this.timer = setTimeout(() => {
        this.pagination.page = 1
        this.getRooms()
      }, 800)
    },
    showAll () {
      this.pagination.showAll = !this.pagination.showAll
      this.pagination.page = 1
      this.pagination.itemsPerPage = this.pagination.showAll ? 10000 : 50
      this.getRooms()
    }
  },
  mounted () {
    this.audio = new Audio('https://storage.googleapis.com/keybe/app/audio/notification.mp3')
  },
  watch: {
    displayMethod (value) {
      if (value === 'withAdvisor') this.unreadMessages = 0
    },
    socket (value) {
      if (!value) return
      this.initSocketEvents()
    }
  }
}
</script>

<style lang="scss">
  @import "../../../styles/Conversations/ChatsContainer.scss";
</style>
